import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import BackOfficeLayout from "./BackOfficeLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Users,
  UserPlus,
  Trash2,
  Mail,
  Lock,
  User,
  Loader2,
  Shield,
  Calendar,
  ClipboardCheck,
  UserCog,
  ArrowUpRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BackOfficeUsers = () => {
  const { token, user } = useAuth();
  
  const [users, setUsers] = useState([]);
  const [grouped, setGrouped] = useState({});
  const [loading, setLoading] = useState(true);
  const [createLoading, setCreateLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: "loan_officer"
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/internal-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
      setGrouped(response.data.grouped || {});
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (newUser.password !== newUser.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    
    if (newUser.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setCreateLoading(true);
    
    try {
      await axios.post(`${API}/admin/create-internal-user`, {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role: newUser.role
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${newUser.role.replace("_", " ")} created successfully!`);
      setDialogOpen(false);
      setNewUser({ name: "", email: "", password: "", confirmPassword: "", role: "loan_officer" });
      fetchUsers();
    } catch (error) {
      console.error("Failed to create user:", error);
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail, userRole) => {
    if (!window.confirm(`Are you sure you want to delete ${userRole.replace("_", " ")} ${userEmail}?`)) {
      return;
    }
    
    setDeleteLoading(userId);
    
    try {
      await axios.delete(`${API}/admin/internal-user/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error("Failed to delete user");
    } finally {
      setDeleteLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const getRoleBadge = (role) => {
    const configs = {
      loan_officer: { color: "bg-green-100 text-green-700", icon: ClipboardCheck },
      manager: { color: "bg-blue-100 text-blue-700", icon: UserCog },
      executive: { color: "bg-purple-100 text-purple-700", icon: ArrowUpRight }
    };
    const config = configs[role] || { color: "bg-gray-100 text-gray-700", icon: User };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {role.replace("_", " ").toUpperCase()}
      </Badge>
    );
  };

  const renderUserTable = (userList) => (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {userList.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-8 text-gray-500">
              No users found
            </TableCell>
          </TableRow>
        ) : (
          userList.map((u) => (
            <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
              <TableCell className="font-medium">{u.name}</TableCell>
              <TableCell>
                <span className="flex items-center gap-2 text-gray-600">
                  <Mail className="h-4 w-4" />
                  {u.email}
                </span>
              </TableCell>
              <TableCell>{getRoleBadge(u.role)}</TableCell>
              <TableCell>
                <span className="flex items-center gap-2 text-gray-500 text-sm">
                  <Calendar className="h-4 w-4" />
                  {formatDate(u.created_at)}
                </span>
              </TableCell>
              <TableCell className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteUser(u.user_id, u.email, u.role)}
                  disabled={deleteLoading === u.user_id}
                  data-testid={`delete-user-${u.user_id}`}
                >
                  {deleteLoading === u.user_id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                </Button>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
            <p className="text-gray-600">Create and manage internal users</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0A2540] hover:bg-[#0A2540]/90" data-testid="create-user-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Create Internal User</DialogTitle>
                <DialogDescription>
                  Create a new back office user with specific role and permissions.
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="user-role">Role</Label>
                  <Select
                    value={newUser.role}
                    onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                  >
                    <SelectTrigger data-testid="role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="loan_officer">
                        <div className="flex items-center gap-2">
                          <ClipboardCheck className="h-4 w-4 text-green-600" />
                          Loan Officer
                        </div>
                      </SelectItem>
                      <SelectItem value="manager">
                        <div className="flex items-center gap-2">
                          <UserCog className="h-4 w-4 text-blue-600" />
                          Manager
                        </div>
                      </SelectItem>
                      <SelectItem value="executive">
                        <div className="flex items-center gap-2">
                          <ArrowUpRight className="h-4 w-4 text-purple-600" />
                          Executive
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {newUser.role === "loan_officer" && "Reviews applications and makes recommendations"}
                    {newUser.role === "manager" && "Approves loans ≤$5k, escalates higher amounts"}
                    {newUser.role === "executive" && "Final approval authority for all loans"}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user-name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="user-name"
                      type="text"
                      placeholder="John Doe"
                      className="pl-10"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="user-email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="user-email"
                      type="email"
                      placeholder="user@tzurfinances.com"
                      className="pl-10"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                      required
                      data-testid="user-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="user-password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="user-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      required
                      minLength={6}
                      data-testid="user-password-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="user-confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="user-confirm-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={newUser.confirmPassword}
                      onChange={(e) => setNewUser({ ...newUser, confirmPassword: e.target.value })}
                      required
                      minLength={6}
                      data-testid="user-confirm-password-input"
                    />
                  </div>
                </div>
                
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#0A2540] hover:bg-[#0A2540]/90"
                    disabled={createLoading}
                    data-testid="submit-create-user-btn"
                  >
                    {createLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      "Create User"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Loan Officers</p>
                  <p className="text-2xl font-bold text-green-700">{grouped.loan_officers?.length || 0}</p>
                </div>
                <ClipboardCheck className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Managers</p>
                  <p className="text-2xl font-bold text-blue-700">{grouped.managers?.length || 0}</p>
                </div>
                <UserCog className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Executives</p>
                  <p className="text-2xl font-bold text-purple-700">{grouped.executives?.length || 0}</p>
                </div>
                <ArrowUpRight className="h-8 w-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : (
              <Tabs defaultValue="all" className="w-full">
                <div className="border-b px-4">
                  <TabsList className="h-12">
                    <TabsTrigger value="all">All Users ({users.length})</TabsTrigger>
                    <TabsTrigger value="loan_officers">Loan Officers ({grouped.loan_officers?.length || 0})</TabsTrigger>
                    <TabsTrigger value="managers">Managers ({grouped.managers?.length || 0})</TabsTrigger>
                    <TabsTrigger value="executives">Executives ({grouped.executives?.length || 0})</TabsTrigger>
                  </TabsList>
                </div>
                <TabsContent value="all" className="m-0">
                  {renderUserTable(users)}
                </TabsContent>
                <TabsContent value="loan_officers" className="m-0">
                  {renderUserTable(grouped.loan_officers || [])}
                </TabsContent>
                <TabsContent value="managers" className="m-0">
                  {renderUserTable(grouped.managers || [])}
                </TabsContent>
                <TabsContent value="executives" className="m-0">
                  {renderUserTable(grouped.executives || [])}
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeUsers;
